/**
 * Core app translations — the highest-priority dictionary in the resolver.
 * Extracted out of components/GlobalTranslationProvider.tsx so it can be shared
 * by the resolver WITHOUT importing the React provider (avoids a cycle).
 *
 * LEAF MODULE: only imports the Language type. Never import a React hook here.
 *
 * Covers demo scaffolding, chart labels, nav/sidebar chrome, TREMEC compliance,
 * receivables demo, what-if drivers, auth screen, forex, and the compliance
 * defect taxonomy. English rows double as the canonical fallback text.
 */
import type { Language } from '@/contexts/SettingsContext';

export const coreTranslations: Partial<Record<Language, Record<string, string>>> = {
  en: {
    // ── Demo scaffolding + charts (centralized i18n — components read t(), never inline ternaries) ──
    'demo.mode': 'Demo Mode',
    'demo.welcome': 'Welcome {name} from {company} • {persona} persona • Exploring with mock data',
    'demo.showSetup': 'Show Setup Demo',
    'demo.scenario': 'Demo Scenario',
    'demo.personaView': '{persona} View',
    'demo.sampleData': 'Sample data',
    'demo.persona.controller': 'Controller',
    'demo.persona.analyst': 'Analyst',
    'demo.persona.ceo': 'CEO',
    'demo.persona.cfo': 'CFO',
    'demo.persona.ops': 'Operations',
    'demo.company.fallback': 'your company',
    'demo.user.fallback': 'Demo User',
    'demo.scenarioTitle.month_end_crunch':
      'Month-end approaching — cash tight, collections critical',
    'demo.scenarioTitle.growth_decision': 'Growth decision — evaluating a new contract',
    'demo.scenarioTitle.cross_border_optimization':
      'Cross-border optimization — FX timing and payments',
    'demo.scenarioTitle.liquidity_planning': 'Liquidity planning — cash forecast',
    'demo.scenarioHint.month_end_crunch':
      'Focus: Collections opportunities to improve cash position before month-end',
    'demo.scenarioHint.growth_decision':
      'Focus: Working capital analysis for new contract opportunity',
    'demo.scenarioHint.cross_border_optimization':
      'Focus: FX timing and payment optimization for cross-border operations',
    'demo.scenarioHint.liquidity_planning': 'Focus: Cash forecast scenarios for board presentation',
    'chart.fx.usdReceivables': 'USD Receivables',
    'chart.fx.mxnReceivables': 'MXN Receivables',
    'chart.fx.eurReceivables': 'EUR Receivables',
    'chart.fx.gbpReceivables': 'GBP Receivables',
    'chart.fx.mxnUsdRate': 'MXN/USD Rate',
    'chart.fx.costCoverage': 'Cost Coverage',
    'chart.confidence.upperBound': 'Upper Bound',
    'chart.confidence.lowerBound': 'Lower Bound',
    'chart.confidence.forecast': 'Forecast',
    'chart.confidence.actual': 'Actual',
    'chart.waterfall.title': 'Waterfall Cash Flow',
    'chart.waterfall.netChange': 'Net Change',
    'chart.waterfall.totalInflows': 'Total Inflows',
    'chart.period.weekly': 'Weekly',
    'chart.period.monthly': 'Monthly',
    'chart.period.quarterly': 'Quarterly',
    'chart.period.yearly': 'Yearly',
    'chart.availableCredit.utilization': 'Utilization',
    'chart.windrose.title': 'Windrose Insights',
    'chart.windrose.requiresApproval': 'Requires approval',
    'chart.insights.keyInsights': 'Key Insights',
    // ── Chart i18n migration (13 chart components) — NEW keys ──
    // FXExposureWidget
    'chart.fx.title': 'FX Exposure',
    'chart.fx.titleUsdMxn': 'FX Exposure — USD/MXN',
    'chart.fx.titleIntl': 'FX Exposure — International Receivables',
    'chart.fx.cadReceivables': 'CAD Receivables',
    'chart.fx.totalForeignUsdEq': 'Total Foreign (USD eq.)',
    'chart.fx.months': 'months',
    'chart.fx.moIfMxnUp5': 'mo. if MXN +5%',
    'chart.fx.netPositionLongUsd': 'Net position: LONG USD',
    'chart.fx.monthlyMxnCosts': 'Monthly MXN costs',
    // ConfidenceBandChart
    'chart.confidence.confidenceRange90': '90% Confidence Range',
    // WaterfallCashFlowChart + WaterfallChart
    'chart.waterfall.totalOutflows': 'Total Outflows',
    'chart.waterfall.openingBalance': 'Opening Balance',
    'chart.waterfall.receivables': 'Receivables',
    'chart.waterfall.otherIncome': 'Other Income',
    'chart.waterfall.payables': 'Payables',
    'chart.waterfall.payroll': 'Payroll',
    'chart.waterfall.operatingExp': 'Operating Exp',
    'chart.waterfall.capex': 'CAPEX',
    'chart.waterfall.closingBalance': 'Closing Balance',
    'chart.waterfall.runningTotal': 'Running Total',
    'chart.waterfall.export': 'Export',
    'chart.waterfall.total': 'Total',
    'chart.waterfall.increase': 'Increase',
    'chart.waterfall.decrease': 'Decrease',
    // AvailableCreditCard
    'chart.availableCredit.title': 'Available Credit Analysis',
    'chart.availableCredit.retry': 'Retry',
    'chart.availableCredit.refresh': 'Refresh',
    'chart.availableCredit.tryAgain': 'Try Again',
    'chart.availableCredit.errorLoading': 'Error loading credit analysis data',
    'chart.availableCredit.errorDetail':
      'Unable to fetch buyer credit information. This might be due to database permissions.',
    'chart.availableCredit.totalAvailable': 'Total Available',
    'chart.availableCredit.avgUtilization': 'Avg Utilization',
    'chart.availableCredit.creditLimit': 'Credit Limit',
    'chart.availableCredit.available': 'Available',
    'chart.availableCredit.limitNearlyExceeded': 'Credit limit nearly exceeded',
    'chart.availableCredit.noBuyers': 'No buyers with credit limits configured',
    'chart.availableCredit.noBuyersHint': 'Set up buyer credit limits to see credit analysis',
    'chart.availableCredit.riskUnknown': 'Unknown',
    'chart.availableCredit.riskHigh': 'High Risk',
    'chart.availableCredit.riskMedium': 'Medium Risk',
    'chart.availableCredit.riskLow': 'Low Risk',
    // LineTypeLaborCard
    'chart.laborCard.title': 'Labor Costs',
    'chart.laborCard.vsBudget': 'vs Budget',
    'chart.laborCard.headcount': 'Headcount',
    'chart.laborCard.ftes': 'FTEs',
    'chart.laborCard.overtime': 'Overtime',
    'chart.laborCard.aboveThreshold': 'Above threshold',
    'chart.laborCard.alertTitle': 'Line Type Alert',
    'chart.laborCard.alertDetail': 'Manufacturing dept 23% over policy limit',
    'chart.laborCard.clickForBreakdown': 'Click for detailed breakdown',
    // ScenarioPlanningChart
    'chart.scenario.baseline': 'Baseline',
    'chart.scenario.optimistic': 'Optimistic',
    'chart.scenario.pessimistic': 'Pessimistic',
    'chart.scenario.impact': 'Impact',
    'chart.scenario.line': 'Line',
    'chart.scenario.bar': 'Bar',
    // SimplifiedInsightsCard
    'chart.insights.subtitle': 'Important findings and recommendations',
    'chart.insights.impact': 'Impact',
    'chart.insights.summary': 'Summary',
    'chart.insights.summaryHighPriority':
      'high priority items require attention. Focus on cash flow management and payment follow-ups.',
    'chart.insights.item.1.title': 'Cash Flow Risk Warning',
    'chart.insights.item.1.description':
      'Current burn rate 12% higher than budgeted. Working capital tied up in excess inventory.',
    'chart.insights.item.1.impact': '17 days from negative cash position',
    'chart.insights.item.1.action': 'Review inventory levels',
    'chart.insights.item.2.title': 'Payment Cycle Extension',
    'chart.insights.item.2.description':
      'Customer payment delayed by 22 days from milestone. PO-to-Invoice cycle extended by 15%.',
    'chart.insights.item.2.impact': 'Impacts liquidity runway by -9 days',
    'chart.insights.item.2.action': 'Follow up with customer',
    'chart.insights.item.3.title': 'Early Payment Opportunity',
    'chart.insights.item.3.description':
      'Multiple suppliers offering early payment discounts. Potential savings available.',
    'chart.insights.item.3.impact': 'Save up to $3,400 with early payments',
    'chart.insights.item.3.action': 'Review payment schedule',
    'chart.insights.item.4.title': 'Document Verification Required',
    'chart.insights.item.4.description':
      'Multiple invoices pending verification. Compliance review needed.',
    'chart.insights.item.4.impact': 'May delay payment processing',
    'chart.insights.item.4.action': 'Review documents',
    // TrendIndicator
    'chart.trend.days': 'days',
    // WindroseInsightsCard
    'chart.windrose.subtitle': 'AI-powered insights from your financial ecosystem',
    'chart.windrose.target': 'Target',
    'chart.windrose.analyze': 'Analyze',
    'chart.windrose.requestReview': 'Request Review',
    'chart.windrose.takeAction': 'Take Action',
    'chart.windrose.priority': 'Priority',
    'chart.windrose.priority.high': 'High',
    'chart.windrose.priority.medium': 'Medium',
    'chart.windrose.priority.low': 'Low',
    'chart.windrose.telltale.title': 'Unexpected Shipping Fee Detected',
    'chart.windrose.telltale.description':
      'Detected unexpected shipping fee from Supplier A - $9,200. Customer Z mentioned payment delay in recent email thread.',
    'chart.windrose.telltale.target': 'Supplier Fee Analysis',
    'chart.windrose.fullsend.title': 'Payment Cycle Extension Alert',
    'chart.windrose.fullsend.description':
      'Customer Z payment delayed by 22 days from milestone. PO-to-Invoice cycle extended by 15% vs. budget assumptions.',
    'chart.windrose.fullsend.target': 'Payment Collection',
    'chart.windrose.poniente.title': 'Cash Flow Risk Warning',
    'chart.windrose.poniente.description':
      'Current burn rate 12% higher than budgeted. Working capital tied up in excess inventory - 17 days from negative cash.',
    'chart.windrose.poniente.target': 'Cash Management',
    'chart.windrose.levante.title': 'Credit Risk Assessment',
    'chart.windrose.levante.description':
      'EBITDA margin variance may impact credit score. Allianz coverage at risk if payment delays continue.',
    'chart.windrose.levante.target': 'Credit Risk Management',
    // Language
    'language.en': 'English',
    'language.es': 'Español',
    'language.jp': '日本語',
    'language.fr-ca': 'Français (Canada)',
    'language.pt-br': 'Português (Brasil)',

    // Theme
    'theme.light': 'Light',
    'theme.dark': 'Dark',

    // Toast messages
    'toast.languageUpdated': 'Language Updated',
    'toast.languageUpdatedDesc': 'Your language preference has been saved',
    'toast.themeUpdated': 'Theme Updated',
    'toast.themeUpdatedDesc': 'Your theme has been changed to {theme}',
    'toast.connectionsRefreshed': 'Connections Refreshed',
    'toast.connectionsRefreshedDesc': 'All integration statuses have been updated',
    'toast.refreshFailed': 'Refresh Failed',
    'toast.refreshFailedDesc': 'Failed to refresh connection status',

    // Settings
    'settings.languageAppearance': 'Language & Appearance',
    'settings.language': 'Language',
    'settings.theme': 'Theme',
    'settings.colorScheme': 'Color Scheme',
    'settings.simulation': 'Demo Mode',
    'settings.simulationDesc': 'Show sample data for demonstrations',

    // BUG-100: English entries for the same page chrome. Required so the fix is a
    // TRANSLATION and not a hardcode — the US/English path must still render English.
    'analytics.title': 'Advanced Analytics',
    'analytics.subtitle':
      'Comprehensive financial analysis and forecasting with AI-powered insights',
    'documents.title': 'AI Document Processing Center',
    'documents.subtitle': 'Upload and analyze financial documents with advanced AI processing',
    'suppliers.title': 'Supplier Intelligence',
    'suppliers.subtitle': 'AI-powered supplier management and payment optimization',

    // Navigation
    'nav.usmcaCompliance': 'USMCA Compliance',
    'nav.usmcaComplianceDesc': 'Monitor and optimize USMCA compliance',
    'nav.tremecCompliance': 'TREMEC Compliance',
    'nav.tremecComplianceDesc': 'TREMEC supplier compliance command center',
    'nav.secretaryEconomy': 'Secretary of Economy',
    'nav.secretaryEconomyDesc': 'Capital stack analysis for SME financing',

    // Sidebar
    'sidebar.personas': 'Personas',
    'sidebar.arReceivables': 'AR - Receivables',
    'sidebar.apPayables': 'AP - Payables',
    'sidebar.analytics': 'Analytics',
    'sidebar.forex': 'FX',

    // Common
    'common.active': 'Active',

    // Pages
    'pages.activities': 'Activities',
    'pages.priorities': 'Priorities',
    'pages.messages': 'Messages',
    'pages.documents': 'Documents',
    'pages.contracts': 'Contracts',
    'pages.buyers': 'Buyers',
    'pages.receivables': 'Receivables',
    'pages.paymentsIn': 'Payments In',
    'pages.suppliers': 'Suppliers',
    'pages.payables': 'Payables',
    'pages.paymentsOut': 'Payments Out',
    'pages.analytics': 'Analytics',
    'pages.forex': 'Forex',

    // Charts
    'charts.windroseInsights': 'Windrose Insights',
    'charts.aiRecommendations': 'Alisio Recommendations',

    // What-If Simulation — operational drivers (PRD-37, English)
    'whatif.driverDescription':
      'Pull operational levers — labor mix, shifts, overtime — and see them flow to cash',
    'whatif.laborMixGroup': 'Labor Mix',
    'whatif.laborMixGroupDesc': 'The levers a plant controller actually pulls week to week',
    'whatif.netLaborImpact': 'Net labor',
    'whatif.netLaborTooltip':
      'Combined effect of the four labor-mix drivers on total labor cost, fed into the unified working-capital engine.',
    'whatif.driverDirectShare': 'Direct / Indirect Ratio',
    'whatif.driverDirectShareDesc':
      'Share of labor headcount that is direct (vs higher-burden indirect). More direct lowers blended labor cost.',
    'whatif.driverOvertime': 'Overtime Share',
    'whatif.driverOvertimeDesc':
      'Overtime hours as a share of total labor hours. Paid at a premium — cutting it lowers labor cost.',
    'whatif.driverShifts': 'Shift Structure',
    'whatif.driverShiftsDesc':
      'Number of production shifts. Each added shift raises shift-premium and supervision labor but adds capacity.',
    'whatif.driverShiftOne': '1 shift',
    'whatif.driverShiftTwo': '2 shifts',
    'whatif.driverShiftThree': '3 shifts',
    'whatif.driverShiftCapacity': 'adds capacity',
    'whatif.driverContractor': 'Contractor / FTE Mix',
    'whatif.driverContractorDesc':
      'Share of the workforce that is contractor. Higher hourly cost but no fixed benefit/severance — more contractors raises labor cost.',
    'whatif.otherLevers': 'Production & Payment Levers',
    'whatif.otherLeversDesc': 'Production volume and payment-discipline levers',
    'whatif.driverProductionVolume': 'Production Volume',
    'whatif.driverProductionVolumeDesc':
      'Change in production volume / utilization — flows to revenue capacity.',
    'whatif.leanLaborScenario': 'Lean Labor',
    'whatif.leanLaborScenarioDesc': 'Shift to direct, cut overtime, tighten collections',
    'whatif.rampUpScenario': 'Capacity Ramp-Up',
    'whatif.rampUpScenarioDesc': 'Add a shift, flex contractors, lift volume',
    'whatif.noBaseline':
      'No baseline financial data is available yet. Connect or seed your financials to run a what-if simulation — driver outputs will appear once a baseline exists.',
    'whatif.driverInfoBox':
      'Operational drivers (labor mix, shifts, overtime, contractor ratio, production volume) resolve into the same unified working-capital engine the rest of the dashboard uses — so the what-if figures reconcile with the working-capital panel.',

    // Navigation
    'nav.receivables': 'Receivables',
    'nav.payables': 'Payables',
    'nav.paymentsIn': 'Payments In',
    'nav.paymentsOut': 'Payments Out',
    'nav.approvals': 'Approvals',
    'nav.analytics': 'Analytics',
    'nav.forex': 'Forex',
    'nav.documents': 'Documents',
    'nav.dashboard': 'Dashboard',
    'nav.suppliers': 'Suppliers',
    'nav.buyers': 'Buyers',
    'nav.settings': 'Settings',
    'nav.general': 'General',
    'nav.users': 'Users',
    'nav.integrations': 'Integrations',

    // Common
    'common.search': 'Search...',

    // TREMEC Compliance
    'tremecCompliance.title': 'TREMEC Compliance Center',
    'tremecCompliance.live': 'LIVE',
    'tremecCompliance.corridor': 'QRO ↔ Detroit Corridor',
    'tremecCompliance.reviewScore': '2026 Review Score',
    'tremecCompliance.statShipments': 'Shipments',
    'tremecCompliance.ytd': 'YTD',
    'tremecCompliance.statVerified': 'USMCA Verified',
    'tremecCompliance.statTarget': 'Target 95%',
    'tremecCompliance.statCertTime': 'Avg Cert Time',
    'tremecCompliance.statVsManual': 'vs 4.2 hrs manual',
    'tremecCompliance.statReview': '2026 Review Score',
    'tremecCompliance.activityFeed': 'Supplier Activity Feed',
    'tremecCompliance.eventQuote': 'QUOTE',
    'tremecCompliance.eventQuoteReceived': 'Quote Received',
    'tremecCompliance.eventQuoteDetail':
      'USMCA Pre-Qualified — Synchronizer hubs, SAE 8620, 2,400 pcs for TR-9070 DCT. Steel: Ternium MX (originating). Est. RVC: 77.1%. Recommendation: Award PO.',
    'tremecCompliance.eventCfdiDetail':
      'HS 8483.40 verified. SAE 8620 steel from Ternium MX — LME ±3.2%. Steel purchasing: 84.3% originating (threshold 70%)',
    'tremecCompliance.eventSigned': 'Certificate Signed (Pt. 9)',
    'tremecCompliance.eventSignedDetail':
      'Batch #4456 Audit Ready. RVC: 76.2% — Net Cost Method. 75% core part threshold MET. Steel: Ternium MTY → FMB (forge+machine+HT) → TREMEC (assembly).',
    'tremecCompliance.flagged': 'FLAGGED',
    'tremecCompliance.flaggedTitle': 'Origin Alert',
    'tremecCompliance.flaggedDetail':
      "SAE 4140 at MXN $312/unit — 41% below LME. Steel supplier 'Aceros Nacionales' UNVERIFIED. RVC: 68.4% — BELOW 75%.",
    'tremecCompliance.flaggedAction':
      'Action Required — Request mill certificate. If steel origin unverified, batch cannot ship to Novi duty-free. Est. tariff: 2.5% ($840 USD).',
    'tremecCompliance.pass': 'PASS',
    'tremecCompliance.borderline': 'BORDERLINE',
    'tremecCompliance.aboveThreshold': 'Above 75% threshold',
    'tremecCompliance.atThreshold': 'At 75% threshold',
    'tremecCompliance.recentVerifications': 'Recent Verifications',
    'tremecCompliance.supplier': 'Supplier',
    'tremecCompliance.product': 'Product',
    'tremecCompliance.steel': 'Steel',
    'tremecCompliance.status': 'Status',

    // Receivables Demo View
    'recv.title': 'Accounts Receivable',
    'recv.fmb_perspective': 'FMB Perspective',
    'recv.subtitle': 'Forjas del Bajio — Outstanding invoices across TREMEC & Kirchhoff',
    'recv.hero_factored': 'Batch #4456 — Factorable',
    'recv.stat_outstanding': 'Total Outstanding',
    'recv.stat_invoices': 'invoices',
    'recv.stat_avg_dso': 'Weighted Avg DSO',
    'recv.stat_dso_sub': 'Across all buyers',
    'recv.stat_factorable': 'Factorable Amount',
    'recv.stat_verified_invoices': 'verified',
    'recv.stat_flagged': 'Flagged',
    'recv.stat_flagged_sub': 'Compliance issues',
    'recv.aging_title': 'Invoice Aging',
    'recv.current': 'Current',
    'recv.table_title': 'All Outstanding Invoices',
    'recv.col_invoice': 'Invoice',
    'recv.col_buyer': 'Buyer',
    'recv.col_description': 'Description',
    'recv.col_amount': 'Amount',
    'recv.col_terms': 'Terms',
    'recv.col_days_left': 'Days Left',
    'recv.days_to_due': 'Days to due',
    'recv.days': 'days',
    'recv.status_verified': 'Verified',
    'recv.status_flagged': 'Flagged',
    'recv.status_pending': 'Pending',
    'recv.concentration_title': 'Buyer Concentration',
    'recv.concentration_note':
      '93.8% concentration on TREMEC — Kirchhoff added 2019 for diversification',
    'recv.inv_hero': 'Synchronizer Hubs SAE 8620 — Batch #4456',
    'recv.inv_celaya': 'Gear Blanks SAE 4140 — Steel UNVERIFIED',
    'recv.inv_aceros': 'Input Shaft Forgings SAE 8620',
    'recv.inv_troquelados': 'Shift Fork Stampings HSLA',
    'recv.inv_cnc': 'Countershaft Blanks SAE 4340',
    'recv.inv_componentes': 'Clutch Housing Castings A356 Al',
    'recv.inv_metalicos': 'Synchronizer Ring Forgings Brass/Cu',
    'recv.inv_kirchhoff': 'Structural Forgings SAE 1045 — Bracket Blanks',
    'recv.hero_step_sat': 'SAT RFC verified — VIGENTE',
    'recv.hero_step_cfdi': 'CFDI 4.0 uploaded & validated',
    'recv.hero_step_cert': '9-point certificate signed',
    'recv.hero_step_po': 'PO awarded by TREMEC',
    'recv.hero_step_factor': 'NAFIN factoring eligible',

    // ── PRD-118: PYME financing drawer — eligibility + qualitative bank fit ──
    'financing.title': 'Financing',
    'financing.invoice': 'Invoice',
    'financing.buyer': 'Buyer',
    'financing.amount': 'Amount',
    'financing.currency': 'Currency',
    'financing.dueDate': 'Due date',
    'financing.days': 'days',
    'financing.noBanks': 'No banks configured yet for factoring in {currency}.',
    // Hero — qualified
    'financing.hero.qualified.title': 'You qualify for bank factoring',
    'financing.hero.qualified.subtitle':
      'Your verification dossier is complete, so bank factoring is available to you.',
    'financing.hero.qualified.tier': 'Bank factoring ~ TIIE + 2 to 3 points',
    'financing.hero.qualified.cta': 'See participating banks',
    // Hero — not qualified
    'financing.hero.notQualified.title': 'You don’t qualify for bank factoring yet',
    'financing.hero.notQualified.subtitle':
      'Most PYMEs can’t access bank factoring at any price — the binding constraint is eligibility, not rate. Here’s what’s missing.',
    'financing.hero.notQualified.whatsMissing': 'What’s missing to qualify',
    'financing.hero.notQualified.cta': 'Complete the Compliance Jacket',
    'financing.hero.gap':
      'The gap between qualifying (bank) and not (independent market) is the whole business case.',
    'financing.checklist.title': 'Readiness checklist',
    'financing.checklist.note':
      'This is Alisio’s readiness proxy, not a bank’s underwriting standard.',
    // Bank fit
    'financing.banks.title': 'Participating banks — fit, not price',
    'financing.banks.subtitle':
      'Banks are shown by qualitative fit and funding speed, never ranked by rate.',
    'financing.banks.relationship': 'Partner bank for this deployment',
    'financing.banks.pluses': 'Strengths',
    'financing.banks.minuses': 'Considerations',
    'financing.banks.funding': 'Funding',
    'financing.banks.indicativeRange': 'Indicative range',
    'financing.banks.asOf': 'as of',
    // SOFOM tier
    'financing.sofom.title': 'Independent / SOFOM market',
    'financing.sofom.subtitle':
      'What financing costs when you don’t qualify for a bank — so the gap is concrete.',
    'financing.sofom.catNote':
      'CAT (Total Annual Cost) — the CONDUSEF-correct comparator, informative & indicative.',
    // Programs
    'financing.program.cadenas': 'Cadenas Productivas available',
    // Disclosure
    'financing.disclosure.title': 'Important notice',
    'financing.disclosure.indicative':
      'All rates and ranges are indicative, dated, and subject to the bank’s own evaluation.',
    'financing.disclosure.sources':
      'Bank ranges are sourced to Banxico’s “Indicadores Básicos de Créditos a las PYMES”; the market tier reflects named lender/SOFOM bases and CAT bands.',
    'financing.disclosure.noGuarantee':
      'Alisio does not guarantee approval or pricing. Final terms are set by the institution after its own evaluation. No competitor rate is presented as a firm offer.',
  },
  es: {
    // ── Demo scaffolding + charts (centralized i18n) ──
    'demo.mode': 'Modo Demo',
    'demo.welcome':
      'Bienvenido {name} de {company} • perfil {persona} • Explorando con datos de muestra',
    'demo.showSetup': 'Mostrar configuración demo',
    'demo.scenario': 'Escenario Demo',
    'demo.personaView': 'Vista {persona}',
    'demo.sampleData': 'Datos de muestra',
    'demo.persona.controller': 'Controlador',
    'demo.persona.analyst': 'Analista',
    'demo.persona.ceo': 'Director',
    'demo.persona.cfo': 'Director Financiero',
    'demo.persona.ops': 'Operaciones',
    'demo.company.fallback': 'tu empresa',
    'demo.user.fallback': 'Usuario Demo',
    'demo.scenarioTitle.month_end_crunch':
      'Cierre de mes cerca — efectivo ajustado, cobranza crítica',
    'demo.scenarioTitle.growth_decision': 'Decisión de crecimiento — evaluando un nuevo contrato',
    'demo.scenarioTitle.cross_border_optimization':
      'Optimización transfronteriza — tiempos de FX y pagos',
    'demo.scenarioTitle.liquidity_planning': 'Planeación de liquidez — pronóstico de efectivo',
    'demo.scenarioHint.month_end_crunch':
      'Enfoque: Oportunidades de cobranza para mejorar la posición de efectivo antes del cierre de mes',
    'demo.scenarioHint.growth_decision':
      'Enfoque: Análisis de capital de trabajo para una nueva oportunidad de contrato',
    'demo.scenarioHint.cross_border_optimization':
      'Enfoque: Optimización de tiempos de FX y pagos para operaciones transfronterizas',
    'demo.scenarioHint.liquidity_planning':
      'Enfoque: Escenarios de pronóstico de efectivo para la presentación al consejo',
    'chart.fx.usdReceivables': 'Cuentas por cobrar USD',
    'chart.fx.mxnReceivables': 'Cuentas por cobrar MXN',
    'chart.fx.eurReceivables': 'Cuentas por cobrar EUR',
    'chart.fx.gbpReceivables': 'Cuentas por cobrar GBP',
    'chart.fx.mxnUsdRate': 'Tipo de cambio MXN/USD',
    'chart.fx.costCoverage': 'Cobertura de costos',
    'chart.confidence.upperBound': 'Límite superior',
    'chart.confidence.lowerBound': 'Límite inferior',
    'chart.confidence.forecast': 'Pronóstico',
    'chart.confidence.actual': 'Real',
    'chart.waterfall.title': 'Flujo de efectivo (cascada)',
    'chart.waterfall.netChange': 'Cambio neto',
    'chart.waterfall.totalInflows': 'Entradas totales',
    'chart.period.weekly': 'Semanal',
    'chart.period.monthly': 'Mensual',
    'chart.period.quarterly': 'Trimestral',
    'chart.period.yearly': 'Anual',
    'chart.availableCredit.utilization': 'Utilización',
    'chart.windrose.title': 'Windrose Insights',
    'chart.windrose.requiresApproval': 'Requiere aprobación',
    'chart.insights.keyInsights': 'Perspectivas clave',
    // ── Chart i18n migration (13 chart components) — NEW keys (español) ──
    // FXExposureWidget
    'chart.fx.title': 'Exposición cambiaria',
    'chart.fx.titleUsdMxn': 'Exposición cambiaria — USD/MXN',
    'chart.fx.titleIntl': 'Exposición cambiaria — Cuentas por cobrar internacionales',
    'chart.fx.cadReceivables': 'Cuentas por cobrar CAD',
    'chart.fx.totalForeignUsdEq': 'Total en divisas (eq. USD)',
    'chart.fx.months': 'meses',
    'chart.fx.moIfMxnUp5': 'meses si MXN +5%',
    'chart.fx.netPositionLongUsd': 'Posición neta: LARGA USD',
    'chart.fx.monthlyMxnCosts': 'Costos mensuales en MXN',
    // ConfidenceBandChart
    'chart.confidence.confidenceRange90': 'Rango de confianza del 90%',
    // WaterfallCashFlowChart + WaterfallChart
    'chart.waterfall.totalOutflows': 'Salidas totales',
    'chart.waterfall.openingBalance': 'Saldo inicial',
    'chart.waterfall.receivables': 'Cuentas por cobrar',
    'chart.waterfall.otherIncome': 'Otros ingresos',
    'chart.waterfall.payables': 'Cuentas por pagar',
    'chart.waterfall.payroll': 'Nómina',
    'chart.waterfall.operatingExp': 'Gastos operativos',
    'chart.waterfall.capex': 'CAPEX',
    'chart.waterfall.closingBalance': 'Saldo final',
    'chart.waterfall.runningTotal': 'Total acumulado',
    'chart.waterfall.export': 'Exportar',
    'chart.waterfall.total': 'Total',
    'chart.waterfall.increase': 'Aumento',
    'chart.waterfall.decrease': 'Disminución',
    // AvailableCreditCard
    'chart.availableCredit.title': 'Análisis de crédito disponible',
    'chart.availableCredit.retry': 'Reintentar',
    'chart.availableCredit.refresh': 'Actualizar',
    'chart.availableCredit.tryAgain': 'Intentar de nuevo',
    'chart.availableCredit.errorLoading': 'Error al cargar los datos de análisis de crédito',
    'chart.availableCredit.errorDetail':
      'No se pudo obtener la información de crédito del comprador. Puede deberse a permisos de la base de datos.',
    'chart.availableCredit.totalAvailable': 'Total disponible',
    'chart.availableCredit.avgUtilization': 'Utilización promedio',
    'chart.availableCredit.creditLimit': 'Límite de crédito',
    'chart.availableCredit.available': 'Disponible',
    'chart.availableCredit.limitNearlyExceeded': 'Límite de crédito casi superado',
    'chart.availableCredit.noBuyers': 'No hay compradores con límites de crédito configurados',
    'chart.availableCredit.noBuyersHint':
      'Configure límites de crédito de compradores para ver el análisis de crédito',
    'chart.availableCredit.riskUnknown': 'Desconocido',
    'chart.availableCredit.riskHigh': 'Riesgo alto',
    'chart.availableCredit.riskMedium': 'Riesgo medio',
    'chart.availableCredit.riskLow': 'Riesgo bajo',
    // LineTypeLaborCard
    'chart.laborCard.title': 'Costos laborales',
    'chart.laborCard.vsBudget': 'vs Presupuesto',
    'chart.laborCard.headcount': 'Plantilla',
    'chart.laborCard.ftes': 'ETC',
    'chart.laborCard.overtime': 'Horas extra',
    'chart.laborCard.aboveThreshold': 'Por encima del umbral',
    'chart.laborCard.alertTitle': 'Alerta de Line Type',
    'chart.laborCard.alertDetail': 'Depto. de manufactura 23% por encima del límite de política',
    'chart.laborCard.clickForBreakdown': 'Haga clic para ver el desglose detallado',
    // ScenarioPlanningChart
    'chart.scenario.baseline': 'Línea base',
    'chart.scenario.optimistic': 'Optimista',
    'chart.scenario.pessimistic': 'Pesimista',
    'chart.scenario.impact': 'Impacto',
    'chart.scenario.line': 'Línea',
    'chart.scenario.bar': 'Barras',
    // SimplifiedInsightsCard
    'chart.insights.subtitle': 'Hallazgos y recomendaciones importantes',
    'chart.insights.impact': 'Impacto',
    'chart.insights.summary': 'Resumen',
    'chart.insights.summaryHighPriority':
      'elementos de alta prioridad requieren atención. Enfóquese en la gestión del flujo de efectivo y el seguimiento de pagos.',
    'chart.insights.item.1.title': 'Advertencia de riesgo de flujo de efectivo',
    'chart.insights.item.1.description':
      'La tasa de consumo actual es 12% mayor a la presupuestada. Capital de trabajo inmovilizado en inventario excedente.',
    'chart.insights.item.1.impact': 'A 17 días de una posición de efectivo negativa',
    'chart.insights.item.1.action': 'Revisar niveles de inventario',
    'chart.insights.item.2.title': 'Extensión del ciclo de pago',
    'chart.insights.item.2.description':
      'Pago del cliente retrasado 22 días respecto al hito. Ciclo OC-a-factura extendido en 15%.',
    'chart.insights.item.2.impact': 'Reduce el margen de liquidez en -9 días',
    'chart.insights.item.2.action': 'Dar seguimiento con el cliente',
    'chart.insights.item.3.title': 'Oportunidad de pago anticipado',
    'chart.insights.item.3.description':
      'Varios proveedores ofrecen descuentos por pago anticipado. Ahorros potenciales disponibles.',
    'chart.insights.item.3.impact': 'Ahorre hasta $3,400 con pagos anticipados',
    'chart.insights.item.3.action': 'Revisar calendario de pagos',
    'chart.insights.item.4.title': 'Se requiere verificación de documentos',
    'chart.insights.item.4.description':
      'Varias facturas pendientes de verificación. Se necesita revisión de cumplimiento.',
    'chart.insights.item.4.impact': 'Puede retrasar el procesamiento de pagos',
    'chart.insights.item.4.action': 'Revisar documentos',
    // TrendIndicator
    'chart.trend.days': 'días',
    // WindroseInsightsCard
    'chart.windrose.subtitle': 'Perspectivas impulsadas por IA de su ecosistema financiero',
    'chart.windrose.target': 'Objetivo',
    'chart.windrose.analyze': 'Analizar',
    'chart.windrose.requestReview': 'Solicitar revisión',
    'chart.windrose.takeAction': 'Tomar acción',
    'chart.windrose.priority': 'Prioridad',
    'chart.windrose.priority.high': 'Alta',
    'chart.windrose.priority.medium': 'Media',
    'chart.windrose.priority.low': 'Baja',
    'chart.windrose.telltale.title': 'Cargo de envío inesperado detectado',
    'chart.windrose.telltale.description':
      'Se detectó un cargo de envío inesperado del Proveedor A - $9,200. El Cliente Z mencionó un retraso de pago en un hilo de correo reciente.',
    'chart.windrose.telltale.target': 'Análisis de cargos del proveedor',
    'chart.windrose.fullsend.title': 'Alerta de extensión del ciclo de pago',
    'chart.windrose.fullsend.description':
      'Pago del Cliente Z retrasado 22 días respecto al hito. Ciclo OC-a-factura extendido en 15% frente a los supuestos del presupuesto.',
    'chart.windrose.fullsend.target': 'Cobranza de pagos',
    'chart.windrose.poniente.title': 'Advertencia de riesgo de flujo de efectivo',
    'chart.windrose.poniente.description':
      'La tasa de consumo actual es 12% mayor a la presupuestada. Capital de trabajo inmovilizado en inventario excedente - a 17 días de efectivo negativo.',
    'chart.windrose.poniente.target': 'Gestión de efectivo',
    'chart.windrose.levante.title': 'Evaluación de riesgo crediticio',
    'chart.windrose.levante.description':
      'La varianza del margen EBITDA puede afectar el puntaje crediticio. La cobertura de Allianz está en riesgo si continúan los retrasos de pago.',
    'chart.windrose.levante.target': 'Gestión de riesgo crediticio',
    'language.en': 'English',
    'language.es': 'Español',
    'language.jp': '日本語',
    'language.fr-ca': 'Français (Canada)',
    'language.pt-br': 'Português (Brasil)',
    'theme.light': 'Claro',
    'theme.dark': 'Oscuro',
    'toast.languageUpdated': 'Idioma Actualizado',
    'toast.languageUpdatedDesc': 'Tu preferencia de idioma ha sido guardada',
    'toast.themeUpdated': 'Tema Actualizado',
    'toast.themeUpdatedDesc': 'Tu tema ha sido cambiado a {theme}',
    'toast.connectionsRefreshed': 'Conexiones Actualizadas',
    'toast.connectionsRefreshedDesc': 'Todos los estados de integración han sido actualizados',
    'toast.refreshFailed': 'Actualización Fallida',
    'toast.refreshFailedDesc': 'Falló al actualizar el estado de conexión',
    'settings.languageAppearance': 'Idioma y Apariencia',
    'settings.language': 'Idioma',
    'settings.theme': 'Tema',
    'settings.colorScheme': 'Esquema de Colores',
    'settings.simulation': 'Modo Demo',
    'settings.simulationDesc': 'Mostrar datos de muestra para demostraciones',

    // BUG-100: page chrome that rendered ENGLISH on the Spanish MX demo. These
    // components already called t('key', 'English fallback') — the mechanism was
    // adopted, the Spanish entry was simply absent, so every one silently fell
    // back to English. Adding the entry is the whole fix for this shape.
    'analytics.title': 'Analíticas Avanzadas',
    'analytics.subtitle':
      'Análisis financiero integral y pronósticos con insights impulsados por IA',
    'documents.title': 'Centro de Procesamiento de Documentos con IA',
    'documents.subtitle':
      'Cargue y analice documentos financieros con procesamiento avanzado de IA',
    'suppliers.title': 'Inteligencia de Proveedores',
    'suppliers.subtitle': 'Gestión de proveedores y optimización de pagos impulsadas por IA',

    // Navigation
    'nav.usmcaCompliance': 'Cumplimiento USMCA',
    'nav.usmcaComplianceDesc': 'Monitorear y optimizar el cumplimiento USMCA',
    'nav.tremecCompliance': 'Cumplimiento TREMEC',
    'nav.tremecComplianceDesc': 'Centro de cumplimiento de proveedores TREMEC',
    'nav.secretaryEconomy': 'Secretaría de Economía',
    'nav.secretaryEconomyDesc': 'Análisis de escalera de capital para financiamiento PYME',

    // Sidebar
    'sidebar.personas': 'Personas',
    'sidebar.arReceivables': 'CC - Cuentas por Cobrar',
    'sidebar.apPayables': 'CP - Cuentas por Pagar',
    'sidebar.analytics': 'Analíticas',
    'sidebar.forex': 'Divisas',

    // Common
    'common.active': 'Activo',

    // Pages
    'pages.dashboard': 'Panel de Control',
    'pages.settings': 'Configuración',
    'pages.activities': 'Actividades',
    'pages.priorities': 'Prioridades',
    'pages.messages': 'Mensajes',
    'pages.documents': 'Documentos',
    'pages.contracts': 'Contratos',
    'pages.buyers': 'Compradores',
    'pages.receivables': 'Cuentas por Cobrar',
    'pages.paymentsIn': 'Pagos Entrantes',
    'pages.suppliers': 'Proveedores',
    'pages.payables': 'Cuentas por Pagar',
    'pages.paymentsOut': 'Pagos Salientes',
    'pages.analytics': 'Analíticas',
    'pages.forex': 'Forex',

    // Charts
    'charts.windroseInsights': 'Insights Windrose',
    'charts.aiRecommendations': 'Recomendaciones Alisio',

    // Navigation
    'nav.receivables': 'Cuentas por Cobrar',
    'nav.payables': 'Cuentas por Pagar',
    'nav.paymentsIn': 'Pagos Entrantes',
    'nav.paymentsOut': 'Pagos Salientes',
    'nav.approvals': 'Aprobaciones',
    'nav.analytics': 'Analíticas',
    'nav.forex': 'Forex',
    'nav.documents': 'Documentos',
    'nav.dashboard': 'Panel de Control',
    'nav.suppliers': 'Proveedores',
    'nav.buyers': 'Compradores',
    'nav.settings': 'Configuración',
    'nav.general': 'General',
    'nav.users': 'Usuarios',
    'nav.integrations': 'Integraciones',

    // Common
    'common.search': 'Buscar...',

    // What-If Simulation (Spanish)
    'whatif.loading': 'Cargando datos financieros...',
    'whatif.title': 'Simulaci\u00f3n Qu\u00e9 Pasar\u00eda Si',
    'whatif.description':
      'Modele impactos financieros con c\u00e1lculos precisos de capital de trabajo',
    'whatif.demoData': 'Datos Demo',
    'whatif.reset': 'Restablecer',
    'whatif.scenarioAdjustments': 'Ajustes de Escenario',
    'whatif.scenarioAdjustmentsDesc':
      'Ajuste los impulsores financieros clave para modelar diferentes escenarios',
    'whatif.revenueGrowth': 'Crecimiento de Ingresos',
    'whatif.revenueGrowthDesc': 'Cambio proyectado en los ingresos anuales',
    'whatif.laborCostChange': 'Cambio en Costos Laborales',
    'whatif.laborCostChangeDesc':
      'Cambio en los costos laborales totales (plantilla y/o compensaci\u00f3n)',
    'whatif.dsoChange': 'Cambio en DSO',
    'whatif.dsoChangeDesc':
      'D\u00edas de Ventas Pendientes - menor es mejor para el flujo de efectivo',
    'whatif.dpoChange': 'Cambio en DPO',
    'whatif.dpoChangeDesc':
      'D\u00edas de Cuentas por Pagar - mayor otorga m\u00e1s capital de trabajo',
    'whatif.optimisticScenario': 'Escenario Optimista',
    'whatif.optimisticScenarioDesc': '15% crecimiento de ingresos, capital de trabajo mejorado',
    'whatif.pessimisticScenario': 'Escenario Pesimista',
    'whatif.pessimisticScenarioDesc': '10% ca\u00edda de ingresos, cobranzas deterioradas',
    'whatif.projectedRevenue': 'Ingresos Proyectados',
    'whatif.fromBaseline': 'desde la l\u00ednea base',
    'whatif.projectedProfit': 'Ganancia Proyectada',
    'whatif.change': 'cambio',
    'whatif.monthlyCashFlow': 'Flujo de Efectivo Mensual',
    'whatif.includesWorkingCapital': 'Incluye impacto de capital de trabajo',
    'whatif.cashConversionCycle': 'Ciclo de Conversi\u00f3n de Efectivo',
    'whatif.impactBreakdown': 'Desglose de Impacto',
    'whatif.impactBreakdownDesc': 'C\u00f3mo cada factor contribuye al cambio total en la ganancia',
    'whatif.workingCapitalAnalysis': 'An\u00e1lisis de Capital de Trabajo',
    'whatif.workingCapitalAnalysisDesc':
      'CCC (Ciclo de Conversi\u00f3n de Efectivo) = D\u00edas para convertir inventario y cuentas por cobrar en efectivo, menos plazos de pago',
    'whatif.baseline': 'L\u00cdNEA BASE',
    'whatif.daysSalesOutstanding': 'D\u00edas de Ventas Pendientes',
    'whatif.timeToCollect': 'Tiempo para cobrar el pago',
    'whatif.daysPayableOutstanding': 'D\u00edas de Cuentas por Pagar',
    'whatif.timeToPaySuppliers': 'Tiempo para pagar a proveedores',
    'whatif.daysInventoryOutstanding': 'D\u00edas de Inventario Pendiente',
    'whatif.timeInventorySits': 'Tiempo que el inventario permanece',
    'whatif.dsoDioDpo': 'DSO + DIO - DPO',
    'whatif.changesApplied': 'Cambios Aplicados',
    'whatif.projected': 'PROYECTADO',
    'whatif.noChange': 'Sin cambio',
    'whatif.fasterCashConversion': 'Conversi\u00f3n de efectivo m\u00e1s r\u00e1pida',
    'whatif.slowerCashConversion': 'Conversi\u00f3n de efectivo m\u00e1s lenta',
    'whatif.workingCapitalImpact': 'Impacto en Capital de Trabajo',
    'whatif.cashFreedUp': 'Efectivo liberado por mejoras en capital de trabajo',
    'whatif.additionalCapitalRequired': 'Capital de trabajo adicional requerido',
    'whatif.fromDsoChanges': 'Por Cambios en DSO',
    'whatif.fromDpoChanges': 'Por Cambios en DPO',
    'whatif.infoBox':
      'Esta simulaci\u00f3n utiliza f\u00f3rmulas financieras precisas incluyendo el impacto del capital de trabajo (cambios en DSO/DPO \u00d7 ingresos diarios), c\u00e1lculos de ganancia (ingresos - COGS - mano de obra), y puntuaci\u00f3n de confianza basada en la magnitud de los cambios.',
    'whatif.waterfallBaselineProfit': 'Ganancia Base',
    'whatif.waterfallRevenueChange': 'Cambio en Ingresos',
    'whatif.waterfallCogsChange': 'Cambio en COGS',
    'whatif.waterfallLaborCostChange': 'Cambio en Costos Laborales',
    'whatif.waterfallWorkingCapital': 'Capital de Trabajo',
    'whatif.waterfallProjectedProfit': 'Ganancia Proyectada',
    'whatif.waterfallYear1CashImpact': 'Impacto de Caja Año 1',
    'whatif.confidenceModerate': 'Cambios moderados - los resultados tienen confianza media',
    'whatif.confidenceExtreme':
      'Cambios extremos - los resultados tienen baja confianza. Considere validar las suposiciones.',
    'whatif.confidence': 'Confianza',
    'whatif.days': 'd\u00edas',
    'whatif.fromBaselineSlider': 'desde la l\u00ednea base',

    // What-If Simulation \u2014 operational drivers (PRD-37, Spanish)
    'whatif.driverDescription':
      'Active las palancas operativas \u2014 combinaci\u00f3n de mano de obra, turnos, horas extra \u2014 y vea c\u00f3mo impactan el efectivo',
    'whatif.laborMixGroup': 'Combinaci\u00f3n de Mano de Obra',
    'whatif.laborMixGroupDesc':
      'Las palancas que un controlador de planta realmente ajusta semana a semana',
    'whatif.netLaborImpact': 'Mano de obra neta',
    'whatif.netLaborTooltip':
      'Efecto combinado de los cuatro impulsores de mano de obra sobre el costo laboral total, integrado al motor unificado de capital de trabajo.',
    'whatif.driverDirectShare': 'Proporci\u00f3n Directa / Indirecta',
    'whatif.driverDirectShareDesc':
      'Porcentaje de la plantilla que es mano de obra directa (frente a la indirecta de mayor carga). M\u00e1s directa reduce el costo laboral combinado.',
    'whatif.driverOvertime': 'Proporci\u00f3n de Horas Extra',
    'whatif.driverOvertimeDesc':
      'Horas extra como porcentaje del total de horas laborales. Se pagan con prima \u2014 reducirlas disminuye el costo laboral.',
    'whatif.driverShifts': 'Estructura de Turnos',
    'whatif.driverShiftsDesc':
      'N\u00famero de turnos de producci\u00f3n. Cada turno adicional aumenta la prima de turno y la mano de obra de supervisi\u00f3n, pero a\u00f1ade capacidad.',
    'whatif.driverShiftOne': '1 turno',
    'whatif.driverShiftTwo': '2 turnos',
    'whatif.driverShiftThree': '3 turnos',
    'whatif.driverShiftCapacity': 'a\u00f1ade capacidad',
    'whatif.driverContractor': 'Combinaci\u00f3n Contratista / Empleado',
    'whatif.driverContractorDesc':
      'Porcentaje de la fuerza laboral que es contratista. Mayor costo por hora pero sin costos fijos de prestaciones/liquidaci\u00f3n \u2014 m\u00e1s contratistas eleva el costo laboral.',
    'whatif.otherLevers': 'Palancas de Producci\u00f3n y Pago',
    'whatif.otherLeversDesc': 'Volumen de producci\u00f3n y palancas de disciplina de pago',
    'whatif.driverProductionVolume': 'Volumen de Producci\u00f3n',
    'whatif.driverProductionVolumeDesc':
      'Cambio en el volumen de producci\u00f3n / utilizaci\u00f3n \u2014 impacta la capacidad de ingresos.',
    'whatif.leanLaborScenario': 'Mano de Obra Eficiente',
    'whatif.leanLaborScenarioDesc': 'Cambiar a directa, reducir horas extra, mejorar cobranza',
    'whatif.rampUpScenario': 'Aumento de Capacidad',
    'whatif.rampUpScenarioDesc': 'A\u00f1adir un turno, flexibilizar contratistas, elevar volumen',
    'whatif.noBaseline':
      'A\u00fan no hay datos financieros de l\u00ednea base disponibles. Conecte o cargue sus finanzas para ejecutar una simulaci\u00f3n \u2014 los resultados de los impulsores aparecer\u00e1n una vez que exista una l\u00ednea base.',
    'whatif.driverInfoBox':
      'Los impulsores operativos (combinaci\u00f3n de mano de obra, turnos, horas extra, proporci\u00f3n de contratistas, volumen de producci\u00f3n) se integran al mismo motor unificado de capital de trabajo que usa el resto del panel \u2014 por lo que las cifras de la simulaci\u00f3n concuerdan con el panel de capital de trabajo.',

    // TREMEC Compliance
    'tremecCompliance.title': 'Centro de Cumplimiento TREMEC',
    'tremecCompliance.live': 'EN VIVO',
    'tremecCompliance.corridor': 'Corredor QRO ↔ Detroit',
    'tremecCompliance.reviewScore': 'Puntuación de Revisión 2026',
    'tremecCompliance.statShipments': 'Envíos',
    'tremecCompliance.ytd': 'Acumulado Anual',
    'tremecCompliance.statVerified': 'USMCA Verificado',
    'tremecCompliance.statTarget': 'Meta 95%',
    'tremecCompliance.statCertTime': 'Tiempo Prom. Cert.',
    'tremecCompliance.statVsManual': 'vs 4.2 hrs manual',
    'tremecCompliance.statReview': 'Puntuación de Revisión 2026',
    'tremecCompliance.activityFeed': 'Actividad de Proveedores',
    'tremecCompliance.eventQuote': 'COTIZACIÓN',
    'tremecCompliance.eventQuoteReceived': 'Cotización Recibida',
    'tremecCompliance.eventQuoteDetail':
      'Pre-Calificado USMCA — Bujes sincronizadores, SAE 8620, 2,400 pzas para TR-9070 DCT. Acero: Ternium MX (originario). RVC Est.: 77.1%. Recomendación: Otorgar OC.',
    'tremecCompliance.eventCfdiDetail':
      'HS 8483.40 verificado. Acero SAE 8620 de Ternium MX — LME ±3.2%. Compra de acero: 84.3% originario (umbral 70%)',
    'tremecCompliance.eventSigned': 'Certificado Firmado (Pt. 9)',
    'tremecCompliance.eventSignedDetail':
      'Lote #4456 Listo para Auditoría. RVC: 76.2% — Método Costo Neto. Umbral 75% para parte central CUMPLIDO. Acero: Ternium MTY → FMB (forja+maquinado+TT) → TREMEC (ensamble).',
    'tremecCompliance.flagged': 'ALERTA',
    'tremecCompliance.flaggedTitle': 'Alerta de Origen',
    'tremecCompliance.flaggedDetail':
      "SAE 4140 a MXN $312/unidad — 41% por debajo del LME. Proveedor de acero 'Aceros Nacionales' NO VERIFICADO. RVC: 68.4% — POR DEBAJO del 75%.",
    'tremecCompliance.flaggedAction':
      'Acción Requerida — Solicitar certificado de molino. Si el origen del acero no se verifica, el lote no puede enviarse a Novi libre de aranceles. Arancel est.: 2.5% ($840 USD).',
    'tremecCompliance.pass': 'APROBADO',
    'tremecCompliance.borderline': 'LÍMITE',
    'tremecCompliance.aboveThreshold': 'Por encima del umbral 75%',
    'tremecCompliance.atThreshold': 'En el umbral 75%',
    'tremecCompliance.recentVerifications': 'Verificaciones Recientes',
    'tremecCompliance.supplier': 'Proveedor',
    'tremecCompliance.product': 'Producto',
    'tremecCompliance.steel': 'Acero',
    'tremecCompliance.status': 'Estado',

    // Receivables Demo View
    'recv.title': 'Cuentas por Cobrar',
    'recv.fmb_perspective': 'Perspectiva FMB',
    'recv.subtitle': 'Forjas del Bajio — Facturas pendientes con TREMEC y Kirchhoff',
    'recv.hero_factored': 'Lote #4456 — Factorizable',
    'recv.stat_outstanding': 'Total Pendiente',
    'recv.stat_invoices': 'facturas',
    'recv.stat_avg_dso': 'DSO Promedio Ponderado',
    'recv.stat_dso_sub': 'Todos los compradores',
    'recv.stat_factorable': 'Monto Factorizable',
    'recv.stat_verified_invoices': 'verificadas',
    'recv.stat_flagged': 'Marcadas',
    'recv.stat_flagged_sub': 'Problemas de cumplimiento',
    'recv.aging_title': 'Antigüedad de Facturas',
    'recv.current': 'Vigente',
    'recv.table_title': 'Todas las Facturas Pendientes',
    'recv.col_invoice': 'Factura',
    'recv.col_buyer': 'Comprador',
    'recv.col_description': 'Descripción',
    'recv.col_amount': 'Monto',
    'recv.col_terms': 'Términos',
    'recv.col_days_left': 'Días Rest.',
    'recv.days_to_due': 'Días al vencimiento',
    'recv.days': 'días',
    'recv.status_verified': 'Verificado',
    'recv.status_flagged': 'Marcado',
    'recv.status_pending': 'Pendiente',
    'recv.concentration_title': 'Concentración de Compradores',
    'recv.concentration_note':
      '93.8% concentración en TREMEC — Kirchhoff agregado en 2019 para diversificación',
    'recv.inv_hero': 'Mazas de Sincronizador SAE 8620 — Lote #4456',
    'recv.inv_celaya': 'Blancos de Engrane SAE 4140 — Acero SIN VERIFICAR',
    'recv.inv_aceros': 'Forjas de Flecha de Entrada SAE 8620',
    'recv.inv_troquelados': 'Estampados de Horquilla HSLA',
    'recv.inv_cnc': 'Blancos de Contraflecha SAE 4340',
    'recv.inv_componentes': 'Fundiciones de Campana de Clutch A356 Al',
    'recv.inv_metalicos': 'Forjas de Anillo Sincronizador Latón/Cu',
    'recv.inv_kirchhoff': 'Forjas Estructurales SAE 1045 — Brackets',
    'recv.hero_step_sat': 'RFC SAT verificado — VIGENTE',
    'recv.hero_step_cfdi': 'CFDI 4.0 subido y validado',
    'recv.hero_step_cert': 'Certificado de 9 puntos firmado',
    'recv.hero_step_po': 'OC otorgada por TREMEC',
    'recv.hero_step_factor': 'Elegible para factoraje NAFIN',
    // ── BUG-100 — Spanish entries for keys that resolved to their English
    // fallback on the MX demo. The call sites were already correct; only the
    // 'es' row was absent, so t(key,'English') silently returned English.

    'analytics.alerts.newAlert': 'Nueva Alerta',
    'analytics.alerts.none':
      'No hay alertas activas. La posición de efectivo, la proporción vencida, el DSO y las cuentas por pagar próximas están dentro de sus umbrales.',
    'analytics.alerts.title': 'Gestión de Alertas',
    'analytics.analyzing': 'Analizando...',
    'analytics.dialogs.scenario.assumptionsPlaceholder':
      'Ingrese los supuestos clave, uno por línea',
    'analytics.dialogs.scenario.cancel': 'Cancelar',
    'analytics.dialogs.scenario.costReduction': 'Reducción de Costos (%)',
    'analytics.dialogs.scenario.description':
      'Defina los parámetros para el análisis de escenarios',
    'analytics.dialogs.scenario.descriptionLabel': 'Descripción',
    'analytics.dialogs.scenario.dsoReduction': 'Reducción de DSO (días)',
    'analytics.dialogs.scenario.keyAssumptions': 'Supuestos Clave',
    'analytics.dialogs.scenario.revenueIncrease': 'Incremento de Ingresos (%)',
    'analytics.dialogs.scenario.runSimulation': 'Ejecutar Simulación',
    'analytics.dialogs.scenario.scenarioName': 'Nombre del Escenario',
    'analytics.dialogs.scenario.title': 'Crear Nuevo Escenario',
    'analytics.exportCSV': 'Exportar CSV',
    'analytics.insights.agingCurrent': 'vigente',
    'analytics.insights.atRisk': 'En riesgo',
    'analytics.insights.days': 'días',
    'analytics.insights.daysOverdue': 'vencidas',
    'analytics.insights.description':
      'Análisis de crédito y recomendaciones de cartera impulsados por IA',
    'analytics.insights.excellent': 'Excelente',
    'analytics.insights.fair': 'Regular',
    'analytics.insights.invoices': 'facturas',
    'analytics.insights.noOverdue':
      '• Sin cuentas por cobrar vencidas — nada que gestionar en este ciclo.',
    'analytics.insights.noOverdueToAnalyse':
      'No hay cuentas por cobrar vencidas que analizar para esta cuenta.',
    'analytics.insights.ofReceivablesCurrent': 'de las cuentas por cobrar está vigente',
    'analytics.insights.overdueExposure': 'Exposición Vencida',
    'analytics.insights.overdueOverall': 'vencido en total',
    'analytics.insights.pastDueAcross': 'vencido en',
    'analytics.insights.portfolioHealth': 'Salud de la Cartera',
    'analytics.insights.recommendations': 'Recomendaciones de IA',
    'analytics.insights.riskDistributionLabel': 'Distribución de Riesgo',
    'analytics.insights.showingTop': 'mostrando las principales',
    'analytics.insights.title': 'Insights de Levante IA',
    'analytics.insights.totalReceivables': 'cuentas por cobrar totales',
    'analytics.metrics.basisCashFlow': 'Cuentas por cobrar menos cuentas por pagar',
    'analytics.metrics.basisDso': 'Antigüedad promedio de las facturas abiertas',
    'analytics.metrics.basisReceivables': 'Facturas abiertas, netas de pagos recibidos',
    'analytics.metrics.basisRisk': 'Proporción de cuentas por cobrar no vencidas',
    'analytics.metrics.cashFlow': 'Flujo de Efectivo',
    'analytics.metrics.creditRiskScore': 'Puntaje de Riesgo Crediticio',
    'analytics.metrics.days': 'días',
    'analytics.metrics.dso': 'Días de Ventas Pendientes',
    'analytics.metrics.totalReceivables': 'Cuentas por Cobrar Totales',
    'analytics.portfolioAnalysis': 'Análisis de Cartera',
    'analytics.refresh': 'Actualizar',
    'analytics.reports.download': 'Descargar',
    'analytics.reports.generateReport': 'Generar Informe',
    'analytics.reports.title': 'Informes y Analíticas',
    'analytics.riskDistribution': 'Distribución de Riesgo',
    'analytics.scenarios.cashFlowImpact': 'Impacto en Flujo de Efectivo:',
    'analytics.scenarios.newScenario': 'Nuevo Escenario',
    'analytics.scenarios.revenueImpact': 'Impacto en Ingresos:',
    'analytics.scenarios.riskLevel': 'Nivel de Riesgo:',
    'analytics.scenarios.title': 'Escenarios Qué Pasaría Si',
    'analytics.tabs.alerts': 'Alertas',
    'analytics.tabs.overview': 'Resumen',
    'analytics.tabs.reports': 'Informes',
    'analytics.tabs.scenarios': 'Escenarios',
    'documents.actions.action': 'Acción',
    'documents.actions.execute': 'Ejecutar',
    'documents.actions.status': 'Estado',
    'documents.analytics.aiAccuracy': 'Precisión de la IA',
    'documents.analytics.avgConfidence': 'Confianza Promedio',
    'documents.analytics.avgProcessing': 'Procesamiento Promedio',
    'documents.analytics.documentType': 'Tipo de documento',
    'documents.analytics.documents': 'Documentos',
    'documents.analytics.mostCommon': 'Más Común',
    'documents.analytics.none': 'Ninguno',
    'documents.analytics.perDocument': 'Por documento',
    'documents.analytics.totalProcessed': 'Total Procesados',
    'documents.analytics.typeDistribution': 'Distribución por Tipo de Documento',
    'documents.empty.noHistory': 'No hay documentos en el historial',
    'documents.empty.noResults': 'Aún no hay resultados de procesamiento',
    'documents.empty.uploadPrompt':
      'Cargue un documento para ver los resultados de extracción de la IA',
    'documents.empty.uploadToSeeHistory': 'Cargue y procese documentos para verlos aquí',
    'documents.fields.amount': 'Monto',
    'documents.fields.date': 'Fecha',
    'documents.fields.documentNumber': 'Número de Documento',
    'documents.fields.vendor': 'Proveedor',
    'documents.history.allTypes': 'Todos los Tipos',
    'documents.history.clearHistory': 'Borrar Historial',
    'documents.history.filterByType': 'Filtrar por tipo',
    'documents.history.searchPlaceholder':
      'Buscar documentos, proveedores o números de documento...',
    'documents.invalidFile': 'Archivo Inválido',
    'documents.invalidFiles': 'Archivos Inválidos',
    'documents.modal.documentDetails': 'Detalles del Documento',
    'documents.modal.downloadResults': 'Descargar Resultados',
    'documents.modal.extractedData': 'Datos Extraídos',
    'documents.modal.reprocess': 'Reprocesar',
    'documents.modal.suggestedActions': 'Acciones Sugeridas',
    'documents.new': 'Nuevo',
    'documents.processingQueue': 'Cola de Procesamiento',
    'documents.results.description':
      'Datos extraídos por IA y acciones sugeridas de su documento más reciente',
    'documents.results.executedActions': 'Acciones Ejecutadas',
    'documents.results.lineItems': 'Partidas Extraídas',
    'documents.results.suggestedActions': 'Acciones Sugeridas por IA',
    'documents.results.title': 'Últimos Resultados de Procesamiento',
    'documents.settings.autoExecute': 'Ejecutar Acciones Automáticamente',
    'documents.settings.autoExecuteDesc':
      'Ejecutar automáticamente las sugerencias de IA de alta confianza',
    'documents.settings.batchProcessing': 'Habilitar Procesamiento por Lotes',
    'documents.settings.batchProcessingDesc':
      'Permitir el procesamiento de varios documentos a la vez',
    'documents.settings.confidenceThreshold': 'Umbral de Confianza',
    'documents.settings.description':
      'Configure el comportamiento del procesamiento de documentos con IA y los ajustes de automatización',
    'documents.settings.docsCount': '5 documentos',
    'documents.settings.maxBatchSize': 'Tamaño Máximo de Lote',
    'documents.settings.maxBatchSizeDesc': 'Número máximo de documentos a procesar simultáneamente',
    'documents.settings.title': 'Configuración de Procesamiento',
    'documents.status.complete': 'Procesamiento completo',
    'documents.status.extracting': 'La IA está extrayendo datos...',
    'documents.status.failed': 'Falló el procesamiento',
    'documents.table.description': 'Descripción',
    'documents.table.qty': 'Cant.',
    'documents.table.total': 'Total',
    'documents.table.unitPrice': 'Precio Unitario',
    'documents.tabs.analytics': 'Analíticas',
    'documents.tabs.history': 'Historial',
    'documents.tabs.results': 'Resultados de Procesamiento',
    'documents.tabs.settings': 'Configuración',
    'documents.tabs.upload': 'Cargar y Procesar',
    'documents.upload.aiPowered':
      '✨ Impulsado por IA: clasificación automática, extracción de entidades y sugerencias de acciones',
    'documents.upload.aiProcessing': 'La IA está procesando los documentos...',
    'documents.upload.browseFiles': 'Examinar Archivos',
    'documents.upload.description':
      'Cargue facturas, órdenes de compra, documentos de pago o certificados de comercio exterior para extracción inteligente y sugerencias de acciones.',
    'documents.upload.dragDropPrompt':
      'Arrastre y suelte documentos aquí, o haga clic para seleccionar archivos',
    'documents.upload.dropFiles': 'Suelte los archivos aquí...',
    'documents.upload.supportedFormats':
      'Compatible con: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX (máx. 10MB por archivo)',
    'documents.upload.telltaleIntelligence':
      'Usando la inteligencia de TellTale para extracción y clasificación',
    'documents.upload.title': 'Procesamiento de Documentos con IA',
    'nav.allApprovals': 'Todas las Aprobaciones',
    'nav.allBuyers': 'Todos los Compradores',
    'nav.allDocuments': 'Todos los Documentos',
    'nav.allSuppliers': 'Todos los Proveedores',
    'nav.approvalHistory': 'Historial',
    'nav.buyerAnalytics': 'Analíticas',
    'nav.buyerContracts': 'Contratos',
    'nav.complianceStatus': 'Estado de Cumplimiento',
    'nav.documentAnalytics': 'Analíticas',
    'nav.hedging': 'Cobertura',
    'nav.pendingApprovals': 'Pendientes',
    'nav.rates': 'Tipos de Cambio',
    'nav.supplierAnalytics': 'Analíticas',
    'nav.supplierContracts': 'Contratos',
    'nav.supplierMapping': 'Mapeo de Proveedores',
    'nav.supplierOnboarding': 'Alta de Proveedores',
    'nav.usmcaDashboard': 'Panel USMCA',
    'recommendations.analyzing': 'Analizando sus datos financieros...',
    'recommendations.critical': 'CRÍTICO',
    'recommendations.error': 'Error al cargar las recomendaciones. Por favor, inténtelo de nuevo.',
    'recommendations.noRecommendations':
      'No hay recomendaciones en este momento. ¡Su posición financiera se ve bien!',
    'recommendations.takeAction': 'Tomar Acción',
    'whatif.liveImpact': 'Impacto en Vivo',
    'whatif.updatesAsYouDrag': 'Se actualiza conforme ajusta',
    'payables.actions.escalated': 'Cobranza escalada',
    'payables.actions.paymentsScheduled': 'Pagos programados exitosamente',
    'payables.actions.remindersSent': 'Recordatorios de pago enviados',
    'payables.demoMode': 'Modo Demo',
    'paymentsIn.goToReceivables': 'Ir a Cuentas por Cobrar',
    'paymentsIn.mergedDescription':
      'El seguimiento de pagos, el análisis de antigüedad y las herramientas de cobranza se consolidaron en la página de Cuentas por Cobrar para ofrecer una vista unificada de su cartera.',
    'paymentsIn.mergedNotice': 'Los Pagos Entrantes ahora forman parte de Cuentas por Cobrar',
    // ── BUG-100 — sign-in screen and FX surfaces. These had NO localisation at
    // all (hardcoded English literals), so the call sites were wired to t() in
    // this same pass; these are their Spanish entries.

    'auth.signIn': 'Iniciar Sesión',
    'auth.email': 'Correo electrónico',
    'auth.password': 'Contraseña',
    'auth.rememberMe': 'Recordarme',
    'auth.sending': 'Enviando...',
    'auth.forgotPassword': '¿Olvidó su contraseña?',
    'auth.or': 'O',
    'auth.signInWithGoogle': 'Iniciar sesión con Google',
    'auth.tryLiveDemo': 'Probar la Demo en Vivo',
    'auth.noAccount': '¿No tiene una cuenta?',
    'auth.createAccount': 'Crear cuenta',
    'auth.signingIn': 'Iniciando su sesión...',
    'auth.loginSuccess': '¡Sesión iniciada correctamente!',
    'auth.loginError': 'Ocurrió un error inesperado al iniciar sesión',
    'auth.googleError': 'No se pudo iniciar sesión con Google',
    'auth.enterEmailFirst':
      'Primero ingrese su correo electrónico y luego haga clic en ¿Olvidó su contraseña?',
    'auth.resetLinkSent':
      'Si existe una cuenta con ese correo, se ha enviado un enlace para restablecer la contraseña.',
    'auth.resetUnavailable':
      'El restablecimiento de contraseña aún no está disponible. Por favor, contacte a soporte.',
    'auth.demoCreated': '¡Cuenta demo creada! Bienvenido a Alisio.',
    'forex.hedgingSimulator': 'Simulador de Cobertura Cambiaria',
    'forex.hedgingPlaceholder':
      'Las funciones de simulación de cobertura cambiaria se implementarán aquí.',
    'forex.transactionManager': 'Gestor de Operaciones Cambiarias',
    'forex.transactionPlaceholder':
      'Las funciones de gestión de operaciones cambiarias se implementarán aquí.',
    'forex.ratesMonitor': 'Monitor de Tipos de Cambio',
    'forex.refreshRates': 'Actualizar Tipos de Cambio',
    'forex.bid': 'Compra',
    'forex.ask': 'Venta',
    'forex.lastUpdated': 'Última actualización:',
    'forex.rateAlerts': 'Alertas de Tipo de Cambio',
    'forex.rateAlertsDesc':
      'Configure alertas para recibir una notificación cuando los tipos de cambio alcancen sus niveles objetivo.',
    'forex.configureAlerts': 'Configurar Alertas',
    'forex.realTime': 'En tiempo real',
    'forex.minutesAgoSuffix': 'm atrás',
    'forex.availableBalance': 'Saldo Disponible',
    'forex.reserved': 'Reservado',
    'forex.totalBalance': 'Saldo Total',
    'forex.dailyChange': 'Cambio Diario',
    'forex.monthlyChange': 'Cambio Mensual',
    'forex.accountNumber': 'Número de Cuenta',
    'forex.underMaintenance': 'Cuenta en mantenimiento',
    'forex.transfer': 'Transferir',
    'forex.convert': 'Convertir',
    'forex.virtualAccounts': 'Cuentas Virtuales',
    'forex.balanceTrackingPrefix': 'Seguimiento de saldos en tiempo real en',
    'forex.balanceTrackingSuffix': 'cuentas virtuales',
    'forex.addAccount': 'Agregar Cuenta',
    'forex.totalBalanceUsd': 'Saldo Total (USD)',
    'forex.activeAccounts': 'Cuentas Activas',
    'forex.allProviders': 'Todos los Proveedores',
    'forex.createVirtualAccount': 'Crear Cuenta Virtual',
    'forex.currency': 'Moneda',
    'forex.provider': 'Proveedor',
    'forex.selectProvider': 'Seleccione un proveedor',
    'forex.accountType': 'Tipo de Cuenta',
    'forex.standardAccount': 'Cuenta Estándar',
    'forex.premiumAccount': 'Cuenta Premium',
    'forex.businessAccount': 'Cuenta Empresarial',
    'forex.initialDeposit': 'Depósito Inicial (Opcional)',
    'forex.cancel': 'Cancelar',
    'forex.creating': 'Creando...',
    'forex.createAccount': 'Crear Cuenta',
    // ── BUG-100 / PRD-79 — the defect taxonomy is read by Mexican compliance
    // teams. A report saying "issuer defect — escalate one tier" in English
    // defeats the feature. Class names, remedies, severities and document
    // labels all resolve here; the English wording stays the fallback.

    'compliance.defect.issuer.label': 'defecto de emisor',
    'compliance.defect.issuer.labelPlural': 'defectos de emisor',
    'compliance.defect.issuer.remedy': 'Escalar un nivel hacia arriba. No se corrige reenviando',
    'compliance.defect.authentication.label': 'defecto de autenticación',
    'compliance.defect.authentication.labelPlural': 'defectos de autenticación',
    'compliance.defect.authentication.remedy': 'Solicitar nuevamente al molino',
    'compliance.defect.origin_mismatch.label': 'discrepancia de origen',
    'compliance.defect.origin_mismatch.labelPlural': 'discrepancias de origen',
    'compliance.defect.origin_mismatch.remedy':
      'Investigar — se trata de un error o de una señal de triangulación',
    'compliance.defect.scope.label': 'defecto de alcance',
    'compliance.defect.scope.labelPlural': 'defectos de alcance',
    'compliance.defect.scope.remedy': 'Solicitar el certificado correcto para las coladas reales',
    'compliance.defect.type.label': 'defecto de tipo',
    'compliance.defect.type.labelPlural': 'defectos de tipo',
    'compliance.defect.type.remedy': 'Solicitar el tipo de documento correcto',
    'compliance.defect.completeness.label': 'defecto de integridad',
    'compliance.defect.completeness.labelPlural': 'defectos de integridad',
    'compliance.defect.completeness.remedy': 'Solicitar certificado corregido',
    'compliance.defect.temporal.label': 'defecto temporal',
    'compliance.defect.temporal.labelPlural': 'defectos temporales',
    'compliance.defect.temporal.remedy': 'Investigar',
    'compliance.severity.critical': 'crítico',
    'compliance.severity.high': 'alto',
    'compliance.severity.medium': 'medio',
    'compliance.escalate': 'escalar',
    'compliance.missing': 'faltante',
    'compliance.inFlight': 'en proceso',
    'compliance.complete': 'completo',
    'compliance.notFixableByResending': 'no se corrige reenviando',
    'compliance.doc.usmca_certification_of_origin': 'Certificación de Origen USMCA',
    'compliance.doc.cfdi_complemento_comercio_exterior':
      'CFDI con Complemento de Comercio Exterior',
    'compliance.doc.pedimento_de_importacion': 'Pedimento de importación del insumo',
    'compliance.doc.manifestacion_de_valor_electronica': 'Manifestación de Valor Electrónica',
    'compliance.doc.certificado_de_molino':
      'Certificado de Molino / certificado de calidad por colada',
    'compliance.doc.bom_net_cost_worksheet':
      'Lista de materiales (BOM) y hoja de costo neto que respalda el cálculo de VCR',
    'compliance.doc.upstream_origin_declarations':
      'Declaraciones de origen de proveedores aguas arriba y de sus propios sub-niveles',
    'compliance.doc.aaips_approval':
      'Autorización AAIPS con el certificado de molino o de calidad adjunto',
    'compliance.doc.packing_list_heat_traceability':
      'Lista de empaque con trazabilidad de número de colada a lote',
    'compliance.doc.anexo_24_descargo_extract': 'Extracto de descargo del Anexo 24',
    'compliance.doc.lvc_worksheet': 'Hoja de cálculo de VCL (LVC)',
    'compliance.doc.ppap_imds': 'Datos de material PPAP / IMDS',
    'compliance.completeness.fileFor': 'Expediente para',
    'compliance.completeness.documentFile': 'Expediente de documentos',
    'compliance.completeness.of': 'de',
    'compliance.completeness.accepted': 'aceptados',
    'compliance.completeness.status': 'Estado',
    'compliance.completeness.missing': 'Faltantes',
    'compliance.completeness.inFlight': 'En proceso',
    'compliance.completeness.complete': 'Expediente completo',
    'compliance.completeness.rulesApplied': 'Reglas aplicadas:',
    'compliance.completeness.fallbackProfile':
      'Sin perfil configurado para este comprador — se muestra el conjunto base canónico.',
    'compliance.assurance.verified': 'verificados',
    'compliance.assurance.asserted': 'declarados',
    'compliance.assurance.unknown': 'desconocidos',

    // ── PRD-118: PYME financing drawer — eligibility + qualitative bank fit ──
    'financing.title': 'Financiamiento',
    'financing.invoice': 'Factura',
    'financing.buyer': 'Comprador',
    'financing.amount': 'Monto',
    'financing.currency': 'Moneda',
    'financing.dueDate': 'Vencimiento',
    'financing.days': 'días',
    'financing.noBanks': 'Aún no hay bancos configurados para factoraje en {currency}.',
    // Hero — califica
    'financing.hero.qualified.title': 'Calificas para factoraje bancario',
    'financing.hero.qualified.subtitle':
      'Tu dossier de cumplimiento está completo, por lo que el factoraje bancario está disponible para ti.',
    'financing.hero.qualified.tier': 'Factoraje bancario ~ TIIE + 2 a 3 puntos',
    'financing.hero.qualified.cta': 'Ver bancos participantes',
    // Hero — no califica
    'financing.hero.notQualified.title': 'Aún no calificas para factoraje bancario',
    'financing.hero.notQualified.subtitle':
      'La mayoría de las PYMEs no accede a factoraje bancario a ningún precio: la restricción es la elegibilidad, no la tasa. Esto es lo que falta.',
    'financing.hero.notQualified.whatsMissing': 'Qué falta para calificar',
    'financing.hero.notQualified.cta': 'Completar el Expediente de Cumplimiento',
    'financing.hero.gap':
      'La diferencia entre calificar (banco) y no calificar (mercado independiente) es todo el caso de negocio.',
    'financing.checklist.title': 'Lista de preparación',
    'financing.checklist.note':
      'Este es el indicador de preparación de Alisio, no el estándar de originación de un banco.',
    // Fit de bancos
    'financing.banks.title': 'Bancos participantes — por ajuste, no por precio',
    'financing.banks.subtitle':
      'Los bancos se muestran por ajuste cualitativo y velocidad de fondeo, nunca ordenados por tasa.',
    'financing.banks.relationship': 'Banco aliado de esta implementación',
    'financing.banks.pluses': 'Fortalezas',
    'financing.banks.minuses': 'Consideraciones',
    'financing.banks.funding': 'Fondeo',
    'financing.banks.indicativeRange': 'Rango indicativo',
    'financing.banks.asOf': 'al',
    // Mercado SOFOM
    'financing.sofom.title': 'Mercado independiente / SOFOM',
    'financing.sofom.subtitle':
      'Cuánto cuesta el financiamiento cuando no calificas con un banco — para que la diferencia sea concreta.',
    'financing.sofom.catNote':
      'CAT (Costo Anual Total) — el comparador correcto según CONDUSEF, informativo e indicativo.',
    // Programas
    'financing.program.cadenas': 'Cadenas Productivas disponible',
    // Aviso
    'financing.disclosure.title': 'Aviso importante',
    'financing.disclosure.indicative':
      'Todas las tasas y rangos son indicativos, con fecha, y sujetos a la evaluación propia del banco.',
    'financing.disclosure.sources':
      'Los rangos bancarios se basan en los «Indicadores Básicos de Créditos a las PYMES» de Banxico; el mercado independiente refleja bases de prestamistas/SOFOM y bandas de CAT.',
    'financing.disclosure.noGuarantee':
      'Alisio no garantiza la aprobación ni el precio. Los términos finales los define la institución tras su propia evaluación. Ninguna tasa de un competidor se presenta como oferta en firme.',
  },
  'fr-ca': {
    'language.en': 'English',
    'language.es': 'Español',
    'language.jp': '日本語',
    'language.fr-ca': 'Français (Canada)',
    'language.pt-br': 'Português (Brasil)',
    'theme.light': 'Clair',
    'theme.dark': 'Sombre',
    'toast.languageUpdated': 'Langue Mise à Jour',
    'toast.languageUpdatedDesc': 'Votre préférence de langue a été sauvegardée',
    'toast.themeUpdated': 'Thème Mis à Jour',
    'toast.themeUpdatedDesc': 'Votre thème a été changé pour {theme}',
    'toast.connectionsRefreshed': 'Connexions Actualisées',
    'toast.connectionsRefreshedDesc': "Tous les statuts d'intégration ont été mis à jour",
    'toast.refreshFailed': 'Actualisation Échouée',
    'toast.refreshFailedDesc': "Échec de l'actualisation du statut de connexion",
    'settings.languageAppearance': 'Langue et Apparence',
    'settings.language': 'Langue',
    'settings.theme': 'Thème',
    'settings.colorScheme': 'Schéma de Couleurs',
    'settings.simulation': 'Mode Démo',
    'settings.simulationDesc': "Afficher des données d'exemple pour les démonstrations",

    // Navigation
    'nav.usmcaCompliance': 'Conformité USMCA',
    'nav.usmcaComplianceDesc': 'Surveiller et optimiser la conformité USMCA',
    'nav.tremecCompliance': 'Conformité TREMEC',
    'nav.tremecComplianceDesc': 'Centre de conformité fournisseurs TREMEC',
    'nav.secretaryEconomy': "Secrétariat de l'Économie",
    'nav.secretaryEconomyDesc': 'Analyse de la pile de capital pour le financement PME',

    // Sidebar
    'sidebar.personas': 'Personas',
    'sidebar.arReceivables': 'CC - Comptes à Recevoir',
    'sidebar.apPayables': 'CP - Comptes à Payer',
    'sidebar.analytics': 'Analytiques',
    'sidebar.forex': 'Devises',

    // Common
    'common.active': 'Actif',

    // Pages
    'pages.activities': 'Activités',
    'pages.priorities': 'Priorités',
    'pages.messages': 'Messages',
    'pages.documents': 'Documents',
    'pages.contracts': 'Contrats',
    'pages.buyers': 'Acheteurs',
    'pages.receivables': 'Comptes à Recevoir',
    'pages.paymentsIn': 'Paiements Entrants',
    'pages.suppliers': 'Fournisseurs',
    'pages.payables': 'Comptes à Payer',
    'pages.paymentsOut': 'Paiements Sortants',
    'pages.analytics': 'Analytiques',
    'pages.forex': 'Forex',

    // Charts
    'charts.windroseInsights': 'Insights Windrose',
    'charts.aiRecommendations': 'Recommandations Alisio',

    // Navigation
    'nav.receivables': 'Comptes à Recevoir',
    'nav.payables': 'Comptes à Payer',
    'nav.paymentsIn': 'Paiements Entrants',
    'nav.paymentsOut': 'Paiements Sortants',
    'nav.approvals': 'Approbations',
    'nav.analytics': 'Analytiques',
    'nav.forex': 'Forex',
    'nav.documents': 'Documents',
    'nav.dashboard': 'Tableau de Bord',
    'nav.suppliers': 'Fournisseurs',
    'nav.buyers': 'Acheteurs',
    'nav.settings': 'Paramètres',
    'nav.general': 'Général',
    'nav.users': 'Utilisateurs',
    'nav.integrations': 'Intégrations',

    // Common
    'common.search': 'Rechercher...',
  },
  'pt-br': {
    'language.en': 'English',
    'language.es': 'Español',
    'language.jp': '日本語',
    'language.fr-ca': 'Français (Canada)',
    'language.pt-br': 'Português (Brasil)',
    'theme.light': 'Claro',
    'theme.dark': 'Escuro',
    'toast.languageUpdated': 'Idioma Atualizado',
    'toast.languageUpdatedDesc': 'Sua preferência de idioma foi salva',
    'toast.themeUpdated': 'Tema Atualizado',
    'toast.themeUpdatedDesc': 'Seu tema foi alterado para {theme}',
    'toast.connectionsRefreshed': 'Conexões Atualizadas',
    'toast.connectionsRefreshedDesc': 'Todos os status de integração foram atualizados',
    'toast.refreshFailed': 'Atualização Falhou',
    'toast.refreshFailedDesc': 'Falha ao atualizar status de conexão',
    'settings.languageAppearance': 'Idioma e Aparência',
    'settings.language': 'Idioma',
    'settings.theme': 'Tema',
    'settings.colorScheme': 'Esquema de Cores',
    'settings.simulation': 'Modo Demo',
    'settings.simulationDesc': 'Mostrar dados de exemplo para demonstrações',

    // Navigation
    'nav.usmcaCompliance': 'Conformidade USMCA',
    'nav.usmcaComplianceDesc': 'Monitorar e otimizar a conformidade USMCA',
    'nav.tremecCompliance': 'Conformidade TREMEC',
    'nav.tremecComplianceDesc': 'Centro de conformidade de fornecedores TREMEC',
    'nav.secretaryEconomy': 'Secretaria da Economia',
    'nav.secretaryEconomyDesc': 'Análise da pilha de capital para financiamento PME',

    // Sidebar
    'sidebar.personas': 'Personas',
    'sidebar.arReceivables': 'CR - Contas a Receber',
    'sidebar.apPayables': 'CP - Contas a Pagar',
    'sidebar.analytics': 'Análises',
    'sidebar.forex': 'Câmbio',

    // Common
    'common.active': 'Ativo',

    // Pages
    'pages.activities': 'Atividades',
    'pages.priorities': 'Prioridades',
    'pages.messages': 'Mensagens',
    'pages.documents': 'Documentos',
    'pages.contracts': 'Contratos',
    'pages.buyers': 'Compradores',
    'pages.receivables': 'Contas a Receber',
    'pages.paymentsIn': 'Pagamentos Recebidos',
    'pages.suppliers': 'Fornecedores',
    'pages.payables': 'Contas a Pagar',
    'pages.paymentsOut': 'Pagamentos Enviados',
    'pages.analytics': 'Análises',
    'pages.forex': 'Câmbio',

    // Charts
    'charts.windroseInsights': 'Insights Windrose',
    'charts.aiRecommendations': 'Recomendações Alisio',

    // Navigation
    'nav.receivables': 'Contas a Receber',
    'nav.payables': 'Contas a Pagar',
    'nav.paymentsIn': 'Pagamentos Recebidos',
    'nav.paymentsOut': 'Pagamentos Enviados',
    'nav.approvals': 'Aprovações',
    'nav.analytics': 'Análises',
    'nav.forex': 'Câmbio',
    'nav.documents': 'Documentos',
    'nav.dashboard': 'Painel de Controle',
    'nav.suppliers': 'Fornecedores',
    'nav.buyers': 'Compradores',
    'nav.settings': 'Configurações',
    'nav.general': 'Geral',
    'nav.users': 'Usuários',
    'nav.integrations': 'Integrações',

    // Common
    'common.search': 'Pesquisar...',
  },
};
